import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Sparkles, Upload, FileText, CheckCircle2, AlertTriangle, Loader2,
  ArrowRight, X, FileStack,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase C polish — Onboarding Wizard at /onboarding
 *
 * Three step flow:
 *  1. Pick — drop or browse one or many files (PDF/DOCX/XLSX/CSV/TXT)
 *  2. Process — each file is sent to /api/upload-extract/analyze sequentially
 *     with a per-file status row (pending → analyzing → ready / failed) and
 *     summary counts. Failures are retryable.
 *  3. Import — single "Import everything" button calls /commit on each
 *     successful analysis with sensible defaults (create new, skip dups,
 *     update discrepancies).
 *
 * Goal: bulk-onboard a service business in ~5 clicks.
 */

const STATUS = {
  pending: { label: "Pending", color: "bg-slate-500/15 text-slate-700 border-slate-300" },
  analyzing: { label: "Analyzing…", color: "bg-blue-500/15 text-blue-700 border-blue-300" },
  ready: { label: "Ready", color: "bg-emerald-500/15 text-emerald-700 border-emerald-300" },
  failed: { label: "Failed", color: "bg-rose-500/15 text-rose-700 border-rose-300" },
  committing: { label: "Importing…", color: "bg-amber-500/15 text-amber-800 border-amber-300" },
  imported: { label: "Imported", color: "bg-emerald-600/20 text-emerald-800 border-emerald-400" },
};

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [items, setItems] = useState([]); // { id, file, status, analysis, error, summary, committed }
  const [committing, setCommitting] = useState(false);
  const fileRef = useRef(null);

  const addFiles = (fileList) => {
    const list = Array.from(fileList || []);
    if (list.length === 0) return;
    const next = list.map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      file: f,
      status: "pending",
      analysis: null,
      error: null,
      committed: null,
    }));
    setItems((prev) => [...prev, ...next]);
  };

  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const beginProcessing = async () => {
    setStep(2);
    // Sequentially process to avoid hammering Claude
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (it.status !== "pending" && it.status !== "failed") continue;
      setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, status: "analyzing", error: null } : p));
      try {
        const fd = new FormData();
        fd.append("file", it.file);
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.post(`${API}/upload-extract/analyze`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, status: "ready", analysis: res.data } : p));
      } catch (e) {
        setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, status: "failed", error: e?.response?.data?.detail || e.message || "Failed" } : p));
      }
    }
  };

  const retryItem = async (id) => {
    const it = items.find((p) => p.id === id);
    if (!it) return;
    setItems((prev) => prev.map((p) => p.id === id ? { ...p, status: "analyzing", error: null } : p));
    try {
      const fd = new FormData();
      fd.append("file", it.file);
      const res = await axios.post(`${API}/upload-extract/analyze`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((prev) => prev.map((p) => p.id === id ? { ...p, status: "ready", analysis: res.data } : p));
    } catch (e) {
      setItems((prev) => prev.map((p) => p.id === id ? { ...p, status: "failed", error: e?.response?.data?.detail || "Failed" } : p));
    }
  };

  // Commit one analysis with default decisions:
  // - clients/employees with proposed_action === 'create' → create
  // - duplicate → skip
  // - discrepancy → update
  const commitOne = async (it) => {
    const ex = it.analysis?.extracted || {};
    const decisions = {
      clients: (ex.clients || []).map((c) => ({
        action: c.proposed_action === "duplicate" ? "skip"
              : c.proposed_action === "discrepancy" ? "update"
              : "create",
        existing_id: c.match?.existing_id,
        name: c.name, email: c.email, phone: c.phone, address: c.address,
      })),
      employees: (ex.employees || []).map((e) => ({
        action: e.proposed_action === "duplicate" ? "skip"
              : e.proposed_action === "discrepancy" ? "update"
              : "create",
        existing_id: e.match?.existing_id,
        name: e.name, email: e.email, phone: e.phone, role: e.role,
      })),
    };
    return axios.post(`${API}/upload-extract/commit`, {
      analysis_id: it.analysis.analysis_id,
      decisions,
    });
  };

  const importAll = async () => {
    setStep(3);
    setCommitting(true);
    let total = { clients_created: 0, employees_created: 0, clients_updated: 0, employees_updated: 0 };
    for (const it of items.filter((p) => p.status === "ready")) {
      setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, status: "committing" } : p));
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await commitOne(it);
        const r = res.data || {};
        total.clients_created += r.created?.clients || 0;
        total.employees_created += r.created?.employees || 0;
        total.clients_updated += r.updated?.clients || 0;
        total.employees_updated += r.updated?.employees || 0;
        setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, status: "imported", committed: r } : p));
      } catch (e) {
        setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, status: "failed", error: e?.response?.data?.detail || "Import failed" } : p));
      }
    }
    setCommitting(false);
    toast.success(`Imported ${total.clients_created} clients · ${total.employees_created} employees`);
  };

  const allReady = items.length > 0 && items.every((it) => it.status === "ready");
  const anyReady = items.some((it) => it.status === "ready");
  const totalSummary = items.reduce((acc, it) => {
    const s = it.analysis?.extracted?.summary || {};
    acc.clients_new += s.clients_new || 0;
    acc.clients_dup += s.clients_dup || 0;
    acc.clients_discrep += s.clients_discrep || 0;
    acc.employees_new += s.employees_new || 0;
    acc.employees_dup += s.employees_dup || 0;
    acc.employees_discrep += s.employees_discrep || 0;
    return acc;
  }, { clients_new: 0, clients_dup: 0, clients_discrep: 0, employees_new: 0, employees_dup: 0, employees_discrep: 0 });

  const progress = items.length === 0 ? 0
    : (items.filter((it) => it.status === "ready" || it.status === "imported").length / items.length) * 100;

  return (
    <div className="p-6 space-y-6" data-testid="onboarding-wizard-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><Sparkles className="h-7 w-7 text-emerald-600" /> Onboarding Wizard</h1>
        <p className="text-muted-foreground mt-1">Bulk-import your business in three steps. Drop your existing client list, employee roster, or any PDFs/Excel sheets and we'll structure them into Business Genie automatically.</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-sm" data-testid="step-indicator">
        {[
          { n: 1, label: "Pick files" },
          { n: 2, label: "Process" },
          { n: 3, label: "Import" },
        ].map((s, i, arr) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${step >= s.n ? "bg-emerald-100 text-emerald-700 border-emerald-400" : "text-muted-foreground"}`} data-testid={`step-${s.n}`}>
              <span className="font-mono">{s.n}</span> {s.label}
            </div>
            {i < arr.length - 1 && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 — pick */}
      {step === 1 && (
        <Card>
          <CardContent className="p-8 space-y-4">
            <div
              className="border-2 border-dashed rounded-lg p-10 text-center cursor-pointer hover:border-emerald-400 transition"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              data-testid="onboarding-dropzone"
            >
              <FileStack className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">Drop multiple files here or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">PDF · DOCX · XLSX · CSV · TXT (max 15 MB each)</p>
              <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.doc,.xlsx,.xlsm,.xls,.csv,.txt,.md" className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="onboarding-file-input" />
            </div>
            {items.length > 0 && (
              <div className="space-y-1" data-testid="picked-files-list">
                {items.map((it) => (
                  <div key={it.id} className="flex items-center justify-between p-2 border rounded-md text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span className="truncate">{it.file.name}</span>
                      <span className="text-xs text-muted-foreground">{(it.file.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeItem(it.id)} className="text-rose-600 h-7 w-7 p-0" data-testid={`remove-file-${it.id}`}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button onClick={beginProcessing} disabled={items.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="begin-processing-btn">
                <Upload className="h-4 w-4 mr-1" /> Process {items.length || 0} file{items.length === 1 ? "" : "s"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2 — process */}
      {step >= 2 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base">Processing</CardTitle>
            <div className="flex items-center gap-2 text-xs">
              <Badge variant="outline">{totalSummary.clients_new} new clients</Badge>
              <Badge variant="outline">{totalSummary.employees_new} new employees</Badge>
              <Badge variant="outline">{totalSummary.clients_dup + totalSummary.employees_dup} duplicates</Badge>
              <Badge variant="outline">{totalSummary.clients_discrep + totalSummary.employees_discrep} discrepancies</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Progress value={progress} className="h-2" />
            <div className="space-y-2" data-testid="processing-list">
              {items.map((it) => (
                <div key={it.id} className="p-3 border rounded-md flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      {it.file.name}
                    </p>
                    {it.error && <p className="text-xs text-rose-700 mt-1">{it.error}</p>}
                    {it.analysis && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {it.analysis.extracted?.summary?.clients_total || 0} clients ·
                        {" "}{it.analysis.extracted?.summary?.employees_total || 0} employees
                      </p>
                    )}
                    {it.committed && (
                      <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        +{it.committed.created?.clients || 0} clients · +{it.committed.created?.employees || 0} employees
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {it.status === "analyzing" || it.status === "committing" ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> : null}
                    <Badge variant="outline" className={STATUS[it.status]?.color}>{STATUS[it.status]?.label || it.status}</Badge>
                    {it.status === "failed" && (
                      <Button size="sm" variant="ghost" onClick={() => retryItem(it.id)} className="h-7 text-xs" data-testid={`retry-${it.id}`}>
                        Retry
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA row */}
            {step === 2 && (
              <div className="flex items-center justify-between gap-2 pt-2">
                <div className="text-xs text-muted-foreground">
                  {allReady ? <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> All files analyzed.</span> :
                   <span>Waiting for all files to finish…</span>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                  <Button onClick={importAll} disabled={!anyReady || committing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-all-btn">
                    {committing && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                    Import everything
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3 — done */}
            {step === 3 && !committing && (
              <div className="pt-4 space-y-2 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" /> Onboarding complete
                </div>
                <p className="text-sm text-muted-foreground">Your data is in. Next steps: review the imported records, then jump into building quotes and invoices.</p>
                <div className="flex justify-center gap-2 pt-2">
                  <Button variant="outline" onClick={() => navigate("/clients")} data-testid="goto-clients-btn">View Clients</Button>
                  <Button variant="outline" onClick={() => navigate("/employees")} data-testid="goto-employees-btn">View Employees</Button>
                  <Button onClick={() => navigate("/dashboard")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="goto-dashboard-btn">Go to Dashboard</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
